// @flow

import * as React from 'react'
import PT from 'prop-types'
import mkUsers from '@vimcar/shared-stores/mobx/users'
import mkDomain from '@vimcar/shared-stores/mobx/DomainSwitch'
import mkCalendar from '@vimcar/shared-stores/mobx/Calendar'
import mkUserinfo from '@vimcar/shared-stores/mobx/Userinfo'
import { store, restore } from '@vimcar/shared-stores/utils/mobx'
import type { Request } from '@vimcar/shared-utils/request'
import { setGlobal } from '@vimcar/shared-utils/bindViewModel'

import mkPersistent from '#stores/PersistentStore'

const STORAGE_KEYS = {
  persistentStore: '__VIMCAR_PERSISTENT__',
}

type Props = {
  children?: React.Node,
}

type Context = {
  request: Request,
}

class StoresProvider extends React.Component<Props> {
  static contextTypes = {
    request: PT.func.isRequired,
  }

  static childContextTypes = {
    users: PT.object,
    userinfo: PT.object,
    domain: PT.object,
    persistentStore: PT.object,
    clearStores: PT.func,
    models: PT.object,
    calendar: PT.object,
  }

  constructor(props: Props, ctx: Context) {
    super(props, ctx)

    this.stores = {
      ...this.readRemoteStores(ctx.request),
      ...this.readLocalStores(),
    }

    // Satisfy the requirements of some FSCOM components, e.g.
    // LocalizationProvider
    setGlobal('models', {
      calendar: this.stores.calendar,
      domain: this.stores.domain,
      users: this.stores.users,
      userinfo: this.stores.userinfo,
    })

    window.addEventListener('unload', this.handleWindowUnload)

    // For debugging purposes
    window.__vimcar_stores__ = this.stores
  }

  getChildContext() {
    return {
      ...this.stores,
      models: {
        domain: this.stores.domain,
        users: this.stores.users,
        userinfo: this.stores.userinfo,
      },
      clearStores: () => {
        window.localStorage.clear()
        window.removeEventListener('unload', this.handleWindowUnload)
        this.storeLocalStores()
        window.document.location = '/'
      },
    }
  }

  handleWindowUnload = () => {
    this.storeRemoteStores()
    this.storeLocalStores()
  }

  readRemoteStores(requestClient: Request) {
    return restore(window.APP_VERSION, window.localStorage, requestClient, {
      users: (request, input, models) => {
        return mkUsers({
          request,
          input,
          authBaseUrl: '/-',
          foxboxBaseUrl: '/-/foxbox',
          identityBaseUrl: '/-/identity',
          notificationsBaseUrl: '/-/notifications',
          shopBaseUrl: '',
          triplabBaseUrl: '',
          models,
        })
      },
      userinfo: (request, input, models) => {
        return mkUserinfo({
          request,
          authBaseUrl: '/-',
          oauthBaseUrl: '/-/oauth',
          models,
        })
      },
      domain: (request, input, models) => {
        return mkDomain({
          request,
          models,
          foxboxBaseUrl: '/-/foxbox',
          domainsBaseUrl: '/-/domains',
        })
      },
      calendar: (request, input, models) => {
        return mkCalendar({
          request,
          calendarBaseUrl: '/-/calendar',
          foxboxBaseUrl: '/-/foxbox',
          models,
        })(input)
      },
    })
  }

  storeRemoteStores() {
    const { users, userinfo, domain, calendar } = this.stores

    store(window.APP_VERSION, window.localStorage, {
      users,
      userinfo,
      domain,
      calendar,
    })
  }

  readLocalStores() {
    return {
      persistentStore: mkPersistent(
        JSON.parse(window.localStorage.getItem(STORAGE_KEYS.persistentStore)),
      ),
    }
  }

  storeLocalStores = () => {
    const { persistentStore } = this.stores
    window.localStorage.setItem(
      STORAGE_KEYS.persistentStore,
      JSON.stringify(persistentStore.toJS()),
    )
  }

  stores: { [string]: Object }

  render() {
    return this.props.children || null
  }
}

export default StoresProvider
